import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Popover,
  TextField,
  Button,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import api from "../../axiosInstance";
import MentorPayoutsTable from "./MentorPayoutsTable";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
);

const cardStyle = {
  borderRadius: 3,
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.6)",
};

const MENTOR_PAYMENTS_FILTER_KEY = "MentorPaymentsDateFilter";

const moneyFmt = (v) =>
  v == null ? "₹0" : `₹${Number(v).toLocaleString("en-IN")}`;

// Card definitions: value comes from the fetched summary via `field`,
// growth badge (if present) comes from `growthField`.
const STAT_DEFS = [
  {
    field: "totalRevenue",
    growthField: "totalRevenueGrowthPercentage",
    label: "Total Revenue",
    icon: <MonetizationOnIcon sx={{ fontSize: 32, color: "#42a5f5" }} />,
    iconBg: "linear-gradient(135deg, #f0f7ff, #bbdefb)",
    borderColor: "#64b5f6",
    glowColor: "rgba(100,181,246,0.18)",
    format: moneyFmt,
  },
  {
    field: "totalSiaRevenue",
    growthField: "totalSiaRevenueGrowthPercentage",
    label: "Total SIA Revenue",
    icon: <AccountBalanceWalletIcon sx={{ fontSize: 32, color: "#66bb6a" }} />,
    iconBg: "linear-gradient(135deg, #f1f8f1, #dcedc8)",
    borderColor: "#81c784",
    glowColor: "rgba(129,199,132,0.18)",
    format: moneyFmt,
  },
  {
    field: "totalMentors",
    growthField: "totalMentorsGrowthPercentage",
    label: "Total Mentors",
    icon: <PeopleAltIcon sx={{ fontSize: 32, color: "#ab47bc" }} />,
    iconBg: "linear-gradient(135deg, #fdf4ff, #f3e5f5)",
    borderColor: "#ce93d8",
    glowColor: "rgba(206,147,216,0.18)",
    format: (v) => v ?? 0,
  },
  {
    field: "pendingPayouts",
    growthField: "pendingPayoutsGrowthPercentage",
    label: "Pending Payouts",
    icon: <PendingActionsIcon sx={{ fontSize: 32, color: "#ff9800" }} />,
    iconBg: "linear-gradient(135deg, #fff8f0, #ffe0b2)",
    borderColor: "#ffb74d",
    glowColor: "rgba(255,183,77,0.18)",
    format: moneyFmt,
  },
  {
    field: "refundAmount",
    growthField: "refundAmountGrowthPercentage",
    label: "Refund Amount",
    icon: <AssignmentReturnIcon sx={{ fontSize: 32, color: "#ef5350" }} />,
    iconBg: "linear-gradient(135deg, #fff2f2, #ffcdd2)",
    borderColor: "#e57373",
    glowColor: "rgba(229,115,115,0.18)",
    format: moneyFmt,
  },
  {
    field: "totalGstAmount",
    growthField: "totalGstAmountGrowthPercentage",
    label: "Total GST Amount",
    icon: <ReceiptLongIcon sx={{ fontSize: 32, color: "#26a69a" }} />,
    iconBg: "linear-gradient(135deg, #e0f7fa, #b2ebf2)",
    borderColor: "#4dd0e1",
    glowColor: "rgba(77,208,225,0.18)",
    format: moneyFmt,
  },
];

// Zero-value defaults, matching the real /admin/mentor-payments/dashboard
// response shape — used only until the live summary loads.
const PLACEHOLDER_SUMMARY = {
  totalRevenue: 0,
  totalRevenueGrowthPercentage: null,
  totalSiaRevenue: 0,
  totalSiaRevenueGrowthPercentage: null,
  totalMentors: 0,
  totalMentorsGrowthPercentage: null,
  pendingPayouts: 0,
  pendingPayoutsGrowthPercentage: null,
  refundAmount: 0,
  refundAmountGrowthPercentage: null,
  totalGstAmount: 0,
  totalGstAmountGrowthPercentage: null,
  revenueTrend: [],
  revenueBreakdown: {
    siaRevenue: 0,
    mentorRevenue: 0,
    gstAmount: 0,
    refundAmount: 0,
  },
};

function MentorPayments() {
  const savedFilter = (() => {
    try {
      return JSON.parse(localStorage.getItem(MENTOR_PAYMENTS_FILTER_KEY)) || {};
    } catch {
      return {};
    }
  })();

  const [activeFilter, setActiveFilter] = useState(
    savedFilter.activeFilter || "1M",
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(savedFilter.startDate || "");
  const [endDate, setEndDate] = useState(savedFilter.endDate || "");
  const [summary, setSummary] = useState(PLACEHOLDER_SUMMARY);

  useEffect(() => {
    localStorage.setItem(
      MENTOR_PAYMENTS_FILTER_KEY,
      JSON.stringify({ activeFilter, startDate, endDate }),
    );
  }, [activeFilter, startDate, endDate]);

  const getDateRange = (filter, customStart, customEnd) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    if (filter === "custom") return { start: customStart, end: customEnd };
    if (filter === "TODAY") return { start: fmt(today), end: fmt(today) };
    if (filter === "YESTERDAY") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: fmt(yesterday), end: fmt(yesterday) };
    }
    const end = fmt(today);
    const start = new Date(today);
    if (filter === "1W") start.setDate(start.getDate() - 7);
    else if (filter === "1M") start.setMonth(start.getMonth() - 1);
    else if (filter === "3M") start.setMonth(start.getMonth() - 3);
    else if (filter === "6M") start.setMonth(start.getMonth() - 6);
    else start.setFullYear(start.getFullYear() - 1);
    return { start: fmt(start), end };
  };

  const { start: resolvedStart, end: resolvedEnd } = getDateRange(
    activeFilter,
    startDate,
    endDate,
  );

  const handleFilterChange = (_, val) => {
    if (val) {
      setActiveFilter(val);
      setAnchorEl(null);
    }
  };

  const handleCustomApply = () => {
    if (startDate && endDate) {
      setActiveFilter("custom");
      setAnchorEl(null);
    }
  };

  // Fetch summary cards + chart data from /admin/mentor-payments/dashboard.
  useEffect(() => {
    api
      .get(`/admin/mentor-payments/dashboard`, {
        params: { startDate: resolvedStart, endDate: resolvedEnd },
      })
      .then((res) => {
        setSummary({ ...PLACEHOLDER_SUMMARY, ...(res.data?.data ?? {}) });
      })
      .catch((err) => {
        console.error("Payments summary fetch error:", err);
        setSummary(PLACEHOLDER_SUMMARY);
      });
  }, [resolvedStart, resolvedEnd]);

  const trendLabels =
    summary.revenueTrend?.length > 0
      ? summary.revenueTrend.map((m) => m.label)
      : ["No data"];
  const trendRevenue =
    summary.revenueTrend?.length > 0
      ? summary.revenueTrend.map((m) => m.totalRevenue)
      : [0];
  const trendSiaRevenue =
    summary.revenueTrend?.length > 0
      ? summary.revenueTrend.map((m) => m.siaRevenue)
      : [0];
  const trendMentorRevenue =
    summary.revenueTrend?.length > 0
      ? summary.revenueTrend.map((m) => m.mentorRevenue)
      : [0];

  const revenueTrendData = {
    labels: trendLabels,
    datasets: [
      {
        label: "Total Revenue (₹)",
        data: trendRevenue,
        // fill: true,
        backgroundColor: "rgba(255,167,38,0.12)",
        borderColor: "#ffa726",
        borderWidth: 2.5,
        pointBackgroundColor: "#ffa726",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        tension: 0.4,
      },
      {
        label: "SIA Revenue (₹)",
        data: trendSiaRevenue,
        // fill: true,
        backgroundColor: "rgba(66,165,245,0.12)",
        borderColor: "#42a5f5",
        borderWidth: 2.5,
        pointBackgroundColor: "#42a5f5",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        tension: 0.4,
      },
      {
        label: "Mentor Revenue (₹)",
        data: trendMentorRevenue,
        // fill: true,
        backgroundColor: "rgba(102,187,106,0.12)",
        borderColor: "#66bb6a",
        borderWidth: 2.5,
        pointBackgroundColor: "#66bb6a",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        tension: 0.4,
      },
    ],
  };

  const revenueTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, font: { size: 12 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ` ${ctx.dataset.label}: ₹${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { callback: (val) => `₹${(val / 1000).toFixed(0)}k` },
      },
    },
  };

  const breakdown = summary.revenueBreakdown ?? {};
  const revenueBreakdownRaw = [
    { label: "SIA Revenue", value: breakdown.siaRevenue ?? 0, color: "#42a5f5" },
    {
      label: "Mentor Revenue",
      value: breakdown.mentorRevenue ?? 0,
      color: "#66bb6a",
    },
    { label: "GST", value: breakdown.gstAmount ?? 0, color: "#26a69a" },
    { label: "Refunds", value: breakdown.refundAmount ?? 0, color: "#ef5350" },
  ];
  const hasBreakdownData = revenueBreakdownRaw.some((d) => d.value > 0);
  const doughnutData = {
    labels: revenueBreakdownRaw.map((d) => d.label),
    datasets: [
      {
        data: hasBreakdownData
          ? revenueBreakdownRaw.map((d) => d.value)
          : [1, 0, 0, 0],
        backgroundColor: revenueBreakdownRaw.map((d) => d.color),
        borderColor: revenueBreakdownRaw.map(() => "#fff"),
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "58%",
    plugins: {
      legend: {
        position: "bottom",
        labels: { padding: 16, font: { size: 12 }, usePointStyle: true },
      },
      tooltip: {
        callbacks: { label: (ctx) => ` ${ctx.label}: ${moneyFmt(ctx.parsed)}` },
      },
    },
  };

  return (
    <>
      <Box sx={{ p: 2, paddingBottom: "20px" }}>
        {/* Date range filter */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              background: "rgba(255,255,255,0.85)",
              border: "1px solid #e8eaf0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 16, color: "#667eea" }} />
            <Typography
              sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#444" }}
            >
              {activeFilter === "custom" && startDate && endDate
                ? `${startDate} – ${endDate}`
                : activeFilter === "TODAY"
                  ? "Today"
                  : activeFilter === "YESTERDAY"
                    ? "Yesterday"
                    : activeFilter === "1W"
                      ? "Last Week"
                      : activeFilter === "1M"
                        ? "Last 1 Month"
                        : activeFilter === "3M"
                          ? "Last 3 Months"
                          : activeFilter === "6M"
                            ? "Last 6 Months"
                            : "Last 1 Year"}
            </Typography>
            <Box sx={{ fontSize: 10, color: "#aaa", ml: 0.5 }}>▼</Box>
          </Box>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { mt: 1, borderRadius: 3, minWidth: 220 } }}
          >
            {[
              { label: "Today", value: "TODAY" },
              { label: "Yesterday", value: "YESTERDAY" },
              { label: "Last Week", value: "1W" },
              { label: "Last 1 Month", value: "1M" },
              { label: "Last 3 Months", value: "3M" },
              { label: "Last 6 Months", value: "6M" },
              { label: "Last 1 Year", value: "1Y" },
            ].map((opt) => (
              <Box
                key={opt.value}
                onClick={() => handleFilterChange(null, opt.value)}
                sx={{
                  px: 2.5,
                  py: 1.2,
                  cursor: "pointer",
                  fontSize: "0.83rem",
                  fontWeight: activeFilter === opt.value ? 700 : 500,
                  color: activeFilter === opt.value ? "#667eea" : "#444",
                  background:
                    activeFilter === opt.value
                      ? "rgba(102,126,234,0.07)"
                      : "#fff",
                }}
              >
                {opt.label}
              </Box>
            ))}
            <Box sx={{ height: "1px", background: "#f0f0f0", mx: 2 }} />
            <Box sx={{ p: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="#aaa"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontSize: "0.68rem",
                }}
              >
                Custom Range
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  mt: 1,
                }}
              >
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startDate }}
                  fullWidth
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setActiveFilter("1M");
                      setAnchorEl(null);
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    fullWidth
                    variant="contained"
                    onClick={handleCustomApply}
                    disabled={!startDate || !endDate}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Popover>
        </Box>

        {/* Stat cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
              lg: "repeat(6, 1fr)",
            },
            gap: 2,
            mb: 3,
          }}
        >
          {STAT_DEFS.map((stat) => (
            <Card
              key={stat.field}
              sx={{
                ...cardStyle,
                boxShadow: `0 4px 24px ${stat.glowColor}, 0 1px 4px rgba(0,0,0,0.06)`,
                borderTop: `4px solid ${stat.borderColor}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": { transform: "translateY(-4px)" },
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      background: stat.iconBg,
                      borderRadius: 2,
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: "0.76rem", lineHeight: 1.25 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ fontSize: "1.4rem", color: "#1a1a2e" }}
                  >
                    {stat.format(summary[stat.field])}
                  </Typography>
                  {summary[stat.growthField] != null && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.2,
                        px: 0.8,
                        py: 0.2,
                        borderRadius: "10px",
                        background:
                          summary[stat.growthField] >= 0
                            ? "#e8f5e9"
                            : "#ffebee",
                      }}
                    >
                      {summary[stat.growthField] >= 0 ? (
                        <TrendingUpIcon
                          sx={{ fontSize: 14, color: "#2e7d32" }}
                        />
                      ) : (
                        <TrendingDownIcon
                          sx={{ fontSize: 14, color: "#c62828" }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color:
                            summary[stat.growthField] >= 0
                              ? "#2e7d32"
                              : "#c62828",
                        }}
                      >
                        {Math.abs(summary[stat.growthField])}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
        <MentorPayoutsTable startDate={resolvedStart} endDate={resolvedEnd} />

        {/* Charts */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
            gap: 2.5,
          }}
        >
          <Card sx={{ ...cardStyle, borderTop: "4px solid #42a5f5" }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                    Revenue Trend
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total vs SIA vs Mentor revenue
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 24, color: "#42a5f5" }} />
              </Box>
              <Box sx={{ height: 280 }}>
                <Line data={revenueTrendData} options={revenueTrendOptions} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ ...cardStyle, borderTop: "4px solid #6a1b9a" }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                  Revenue Breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  SIA revenue vs mentor revenue vs GST vs refunds
                </Typography>
              </Box>
              <Box sx={{ height: 280 }}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
}

export default MentorPayments;
